import React from "react";
import { Link } from "react-router-dom";
import { Trophy, Award, Smartphone, QrCode, Users, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export function SpeechContestInfoPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-16">
      <Link to="/register" className="text-xs uppercase tracking-widest text-brand-red font-semibold hover:underline">← Back to hub</Link>
      <div className="mt-6 flex items-center gap-3 mb-2">
        <Trophy className="w-6 h-6 text-brand-gold" />
        <span className="text-xs uppercase tracking-widest text-brand-red font-semibold">Confirmed program</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-bold" data-testid="speech-info-title">
        Speech Contest
      </h1>
      <p className="text-lg text-muted-foreground mt-4">
        A sub-stage speech contest for Indonesian migrant workers and Japanese learners of Bahasa Indonesia,
        with trophies and prizes for both language categories.
      </p>

      <div className="mt-10 grid md:grid-cols-2 gap-5">
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="font-display font-bold text-xl mb-2">日本語 · Japanese Contest</div>
          <p className="text-sm text-muted-foreground">Open to Indonesian migrant workers. Proficiency levels: N5 → N2 (JLPT).</p>
          <p className="text-sm text-muted-foreground mt-2">Up to 5 finalists per level.</p>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <div className="font-display font-bold text-xl mb-2">Bahasa Indonesia Contest</div>
          <p className="text-sm text-muted-foreground">Open to Japanese learners of Bahasa Indonesia. Ranking by years of study.</p>
          <p className="text-sm text-muted-foreground mt-2">Up to 5 finalists per level.</p>
        </div>
      </div>

      <div className="mt-8 bg-brand-canvas rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-brand-red" />
          <div className="text-xs uppercase tracking-widest text-brand-red font-semibold">Hybrid Judging System</div>
        </div>
        <h3 className="font-display text-2xl font-bold">80% Judges · 20% Audience Voting</h3>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Semi-final and final rounds will use the official <strong>IJFF Voting App</strong>. Audience members can rate
          5 categories to generate a total of 10 points per voter. Only 20% of the total score will come from public
          voting; 80% is decided by an expert panel of prominent language masters in Japan.
        </p>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Button asChild size="lg" className="rounded-full bg-brand-red hover:bg-brand-red-dark text-white" disabled>
          <span>Registration details will be announced soon</span>
        </Button>
        <Button asChild size="lg" variant="outline" className="rounded-full">
          <Link to="/">Back to home</Link>
        </Button>
      </div>
    </div>
  );
}

export function StampRallyInfoPage() {
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-16">
      <Link to="/register" className="text-xs uppercase tracking-widest text-brand-red font-semibold hover:underline">← Back to hub</Link>
      <div className="mt-6 flex items-center gap-3 mb-2">
        <Award className="w-6 h-6 text-brand-gold" />
        <span className="text-xs uppercase tracking-widest text-brand-red font-semibold">Confirmed program</span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-bold" data-testid="stamp-info-title">
        Stamp Rally
      </h1>
      <p className="text-lg text-muted-foreground mt-4">
        Explore the festival, collect points, and win prizes. Every visitor who registers can join the Stamp Rally
        via the <strong>IJFF mobile app</strong>.
      </p>

      <div className="mt-10 grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-border rounded-2xl p-6">
          <QrCode className="w-6 h-6 text-brand-red mb-3" />
          <div className="font-display font-bold text-lg">Scan QR</div>
          <p className="text-sm text-muted-foreground mt-1">Each booth has a unique QR code. Scan to collect points.</p>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <Smartphone className="w-6 h-6 text-brand-red mb-3" />
          <div className="font-display font-bold text-lg">Track in app</div>
          <p className="text-sm text-muted-foreground mt-1">Your profile shows total points and mandatory-tenant progress.</p>
        </div>
        <div className="bg-white border border-border rounded-2xl p-6">
          <Users className="w-6 h-6 text-brand-red mb-3" />
          <div className="font-display font-bold text-lg">Win prizes</div>
          <p className="text-sm text-muted-foreground mt-1">Complete the mandatory tenant list and enter the prize drawing.</p>
        </div>
      </div>

      <div className="mt-8 bg-brand-canvas rounded-2xl p-6 md:p-8">
        <h3 className="font-display text-2xl font-bold">Mandatory Scan Targets</h3>
        <ul className="mt-4 space-y-2 text-sm">
          <li>✔ 5 food-seller tenants</li>
          <li>✔ 4 remittance service providers</li>
          <li>✔ 5 Indonesian Embassy booths</li>
          <li>✔ 6 other booths of your choice</li>
        </ul>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Button asChild size="lg" className="rounded-full bg-brand-red hover:bg-brand-red-dark text-white" disabled>
          <span>IJFF app launches December 2026</span>
        </Button>
        <Button asChild size="lg" variant="outline" className="rounded-full">
          <Link to="/">Back to home</Link>
        </Button>
      </div>
    </div>
  );
}
